import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Descriptions,
  Table,
  Modal,
  Input,
  Select,
  message,
  Space,
  Progress,
  Spin,
} from "antd";
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  EyeOutlined,
  SearchOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PauseOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import apiService from '../services/apiService';

const { Title } = Typography;

const PRDetails = () => {
  const { prNo } = useParams();
  const navigate = useNavigate();
  const [prDetails, setPrDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChecksModal, setShowChecksModal] = useState(false);
  const [validationData, setValidationData] = useState([]);
  const [validationLoading, setValidationLoading] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState("");
  const [approvalNotes, setApprovalNotes] = useState("");
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [approvalError, setApprovalError] = useState("");
  const [setApprovalAuthorityValue] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [hasInvoiceAttachment, setHasInvoiceAttachment] = useState(false);
  const [hasNonEnglishAttachment, setHasNonEnglishAttachment] = useState(false);

  useEffect(() => {
    fetchPRDetails();
    fetchValidationDetails();
  }, [prNo]);

  const fetchPRDetails = async () => {
    try {
      setLoading(true);
      const data = await apiService.getPRDetails(prNo);
      setPrDetails(data);
      setSelectedAssignee(data.assignee_name || "Unassigned");
    } catch (error) {
      message.error('Failed to load PR details');
    } finally {
      setLoading(false);
    }
  };

  // Get payment terms from results array
  const getPaymentTerms = () => {
    const paymentTermsField = prDetails?.results?.find(item => item.Field === 'Payment Terms');
    if (paymentTermsField && paymentTermsField.Ariba_API_Value) {
      // Extract first value from semicolon-separated string like "30; 30; 30; 30"
      const firstValue = paymentTermsField.Ariba_API_Value.split(';')[0].trim();
      // Convert to integer to remove decimal places
      const numericValue = parseFloat(firstValue);
      return !isNaN(numericValue) ? Math.floor(numericValue).toString() : firstValue || '--';
    }
    return '--';
  };

  const handleFileDownload = async (fileName) => {
    try {
      const response = await fetch(`http://34.93.4.75/lvp/attachments/${prNo}/${encodeURIComponent(fileName)}?view=false`);
      const data = await response.json();
      console.log('Download response:', data);
      if (data.url) {
        window.location.href = data.url;
      } else {
        message.error('Download URL not available');
      }
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download file');
    }
  };

  const handleFileView = async (fileName) => {
    try {
      const response = await fetch(`http://34.93.4.75/lvp/attachments/${prNo}/${encodeURIComponent(fileName)}?view=true`);
      const data = await response.json();
      console.log('View response:', data);
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        message.error('View URL not available');
      }
    } catch (error) {
      console.error('View error:', error);
      message.error('Failed to view file');
    }
  };

  const fetchValidationDetails = async () => {
    try {
      setValidationLoading(true);
      const data = await apiService.getValidationDetails(prNo);
      const results = data.results || data;

      // Check for invoice-type attachments
      const invoiceDetected = results.some(item =>
        item.Feedback && item.Feedback.toLowerCase().includes("invoice")
      );
      setHasInvoiceAttachment(invoiceDetected);

      // Check for non-English language in Language Detection field
      const langField = results.find(item => item.Field === 'Language Detection');
      const hasNonEnglish = langField &&
        langField.Attachment_Value &&
        !langField.Attachment_Value.replace(/\s/g, '').toLowerCase().startsWith('english:100%');
      setHasNonEnglishAttachment(!!hasNonEnglish);

      const approvalAuthField = results.find(item => item.Field === 'Approval Authority');
      if (approvalAuthField) {
        setApprovalAuthorityValue(approvalAuthField.Attachment_Value || '--');
      }
      
      const formattedData = results
        .filter(item => 
          !item.Field.toLowerCase().includes('commodity') && 
          !item.Field.toLowerCase().includes('approval authority') &&
          !item.Field.toLowerCase().includes('pr region check') &&
          item.Field !== 'Bill To Address' &&
          item.Field !== 'Ship To Address'&&
          item.Field !== 'Bill to Address' &&
          item.Field !== 'Ship to Address'&&
          item.Field !== 'PR Region Check' &&
          item.Field !== 'PR Attachment Count Check'
        )
        .map((item, index) => {
          // Debug log to check Feedback field
          console.log('Field:', item.Field, 'Feedback:', item.Feedback, 'All item keys:', Object.keys(item));
          
          let matchStatus;
          
          // Special handling for HR PR with no attachments
          if (item.Feedback && 
              item.Feedback.includes('Not Applicable (HR PR with no attachments - Ariba data displayed for reference, no validation performed.)')) {
            matchStatus = "Match";
          }
          // Special handling for signature fields with "Not Applicable" (excluding "Unhandled")
          else if (item.Field && item.Field.toLowerCase().includes('signature') && 
              item.Feedback && 
              item.Feedback.toLowerCase().includes('not applicable') && 
              !item.Feedback.toLowerCase().includes('unhandled')) {
            matchStatus = "Match";
          } else {
            // Only use similarity score to determine match status
            // Special threshold for Service Period/Dates field
            const threshold = item.Field === 'Service Period/Dates' ? 0.6 : item.Field === 'CTS Entity Name' ? 0.35 : 0.5;

            if (typeof item.Similarity_Score === 'string' && item.Similarity_Score.includes(';')) {
              // Handle multiple scores, filter out N/A values
              const scores = item.Similarity_Score.split(';')
                .map(score => parseFloat(score.trim()))
                .filter(score => !isNaN(score));
              const hasMatch = scores.some(score => score >= threshold);
              matchStatus = hasMatch ? "Match" : "No Match";
            } else {
              // Handle single score
              const score = parseFloat(item.Similarity_Score);
              matchStatus = !isNaN(score) && score >= threshold ? "Match" : "No Match";
            }
          }
          
          // Format values for Payment Terms and Quantity fields to remove decimals
          let formattedAribaValue = item.Ariba_API_Value;
          let formattedAttachmentValue = item.Attachment_Value;
          
          if (item.Field === 'Payment Terms' || item.Field.startsWith('Quantity_line_item_') || item.Field === 'Quantity') {
            // Remove decimal places for these fields
            if (formattedAribaValue && typeof formattedAribaValue === 'string' && formattedAribaValue.includes(';')) {
              // Handle semicolon-separated values like "30.0; 30.0; 30.0; 30.0"
              formattedAribaValue = formattedAribaValue.split(';').map(val => {
                const num = parseFloat(val.trim());
                return !isNaN(num) ? Math.floor(num).toString() : val.trim();
              }).join('; ');
            } else if (formattedAribaValue && !isNaN(parseFloat(formattedAribaValue))) {
              formattedAribaValue = Math.floor(parseFloat(formattedAribaValue)).toString();
            }
            
            if (formattedAttachmentValue && typeof formattedAttachmentValue === 'string' && formattedAttachmentValue.includes(';')) {
              // Handle semicolon-separated values like "30.0; 30.0; 30.0; 30.0"
              formattedAttachmentValue = formattedAttachmentValue.split(';').map(val => {
                const num = parseFloat(val.trim());
                return !isNaN(num) ? Math.floor(num).toString() : val.trim();
              }).join('; ');
            } else if (formattedAttachmentValue && !isNaN(parseFloat(formattedAttachmentValue))) {
              formattedAttachmentValue = Math.floor(parseFloat(formattedAttachmentValue)).toString();
            }
          }
          
          return {
            key: index + 1,
            field: item.Field,
            fromAriba: formattedAribaValue,
            attachmentValue: formattedAttachmentValue,
            match: matchStatus,
            documentsMatching: item.Attachment_Source,
            remarks: item.Feedback || item.feedback || '--'
          };
        });
      setValidationData(formattedData);
    } catch (error) {
      message.error('Failed to load validation details');
    } finally {
      setValidationLoading(false);
    }
  };

  const handleShowValidation = () => {
    // Navigate to validation page with PR number
    navigate(`/validation/${prNo}`);
  };

  const checksColumns = [
    {
      title: "Field",
      dataIndex: "field",
      key: "field",
      width: 180,
      render: (text) => text === 'Approval Authority (Attachment)' ? 'Approval Authority' : text,
    },
    {
      title: "From Ariba",
      dataIndex: "fromAriba",
      key: "fromAriba",
      width: 130,
    },
    {
      title: "Attachment Value",
      dataIndex: "attachmentValue",
      key: "attachmentValue",
      width: 130,
    },
    {
      title: "Match Status",
      dataIndex: "match",
      key: "match",
      width: 100,
      render: (match) => {
        const color = match === "Match" ? "green" : "red";
        return <span style={{ color }}>{match}</span>;
      },
    },
    {
      title: "Documents Matching",
      dataIndex: "documentsMatching",
      key: "documentsMatching",
      width: 150,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 250,
      render: (text) => (
        <div style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
          {text}
        </div>
      ),
    },
  ];

  const handleApproval = (action) => {
    setApprovalAction(action);
    setApprovalError("");
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async () => {
    if (!approvalNotes.trim()) {
      setApprovalError("Notes are mandatory for this action!");
      return;
    }

    // Map UI action to API action
    const apiAction = approvalAction === "Approve" ? "approve" : approvalAction === "Reject" ? "deny" : null;

    if (!apiAction) {
      // Hold has no API endpoint
      message.info(`PR marked as ${approvalAction}`);
      setShowApprovalModal(false);
      setApprovalNotes("");
      setApprovalError("");
      return;
    }

    try {
      setActionLoading(true);
      const result = await apiService.submitPRAction(prNo, apiAction, approvalNotes);
      message.success(result.message || `PR ${prNo} ${apiAction}d successfully`);
      setShowApprovalModal(false);
      setApprovalNotes("");
      setApprovalError("");
      // Refresh PR details to reflect updated status
      fetchPRDetails();
    } catch (error) {
      if (error.name === 'TimeoutError') {
        message.error('Request timed out. The server is still processing — please check back shortly.');
      } else {
        message.error(error.message || `Failed to ${approvalAction.toLowerCase()} PR`);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssigneeChange = () => {
    message.success(`PR reassigned to ${selectedAssignee}`);
    setShowAssigneeModal(false);
  };

  const assigneeOptions = [
    { value: "Bhaskaran Sekaran", label: "Bhaskaran Sekaran" },
    { value: "B,A Nagashree Shubha", label: "B,A Nagashree Shubha" },
    { value: "M Sridhar Babu ", label: "M Sridhar Babu " },
    { value: "Sadagopan Kulasekaran", label: "Sadagopan Kulasekaran" },
  ];

  const getStatusTag = (status) => {
    if (status === 'On Hold' || status === 'Recommend Hold') {
      return <Tag style={{ backgroundColor: '#faad14', color: 'white', borderColor: '#faad14' }}>{status}</Tag>;
    }
    
    const statusConfig = {
      'Recommend Approve': { color: '#16a34a', bg: '#dcfce7' },
      'Recommend Reject': { color: '#dc2626', bg: '#fee2e2' },
      'Recommend On Hold': { color: '#ea580c', bg: '#fed7aa' },
      'Yet to be Processed': { color: '#6b7280', bg: '#f3f4f6' }
    };

    const config = statusConfig[status] || { color: "default" };
    return <Tag color={config.color}>{status}</Tag>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="Loading PR details..." />
      </div>
    );
  }

  if (!prDetails) {
    return (
      <div className="empty-state">
        <Typography.Text>PR details not found</Typography.Text>
      </div>
    );
  }

  const attachmentsData = prDetails.attachments?.filter(attachment => attachment !== "None" && attachment.trim() !== "").map((attachment, index) => {
    const extension = attachment.split('.').pop().toLowerCase();
    const fileType = extension === 'msg' ? 'email' : extension === 'pdf' ? 'pdf' : 'file';
    const fileIcon = fileType === 'email' ? <MailOutlined style={{ color: "#1890ff" }} /> : <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
    const fileText = fileType === 'email' ? 'EMAIL' : fileType === 'pdf' ? 'PDF' : extension.toUpperCase();
    
    return {
      key: index + 1,
      fileName: attachment,
      type: fileType,
      typeText: fileText,
      icon: fileIcon,
    };
  }) || [];

  const hasAttachments = attachmentsData.length > 0;

  const nestedAttachmentsData = prDetails.nested_attachments?.filter(attachment => attachment !== "None" && attachment.trim() !== "").map((attachment, index) => {
    const extension = attachment.split('.').pop().toLowerCase();
    const fileType = extension === 'msg' ? 'email' : extension === 'pdf' ? 'pdf' : 'file';
    const fileIcon = fileType === 'email' ? <MailOutlined style={{ color: "#1890ff" }} /> : <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
    const fileText = fileType === 'email' ? 'EMAIL' : fileType === 'pdf' ? 'PDF' : extension.toUpperCase();

    return {
      key: index + 1,
      fileName: attachment,
      type: fileType,
      typeText: fileText,
      icon: fileIcon,
    };
  }) || [];

  const hasNestedAttachments = nestedAttachmentsData.length > 0;




  const isUnder50k = parseFloat(prDetails?.total_amount || 0) < 50000;
  const formattedAmount = parseFloat(prDetails?.total_amount || 0).toFixed(2);
  
  // Check if region is Latin America or Europe for amount check exemption
  const region = prDetails.bill_to_region || '';
  const isExemptRegion = region.toLowerCase().includes('latin america') || region.toLowerCase().includes('europe');
  const amountCheckPassed = isUnder50k || isExemptRegion;

  const matchedFields = validationData.filter(
    (item) => item.match === "Match"
  ).length;
  const totalFields = validationData.length;
  const matchPercentage = totalFields > 0 ? Math.round((matchedFields / totalFields) * 100) : 0;

  // Override status to "Recommend Approve" if validation score is 100%
  const displayStatus = matchPercentage === 100 ? "Recommend Approve" : prDetails.validation_status;

  // Get score color based on percentage
  const getScoreColor = (percentage) => {
    if (percentage >= 80) return "#52c41a"; // green
    if (percentage >= 50) return "#faad14"; // orange
    return "#ff4d4f"; // red
  };

  // Get address alignment data
  const addressAlignmentField = validationData.find(
    (item) => item.field === "Ariba Ship_to/Bill_to Address Alignment"
  );
  const addressesMatch = addressAlignmentField?.match === "Match";
  
  // Parse addresses from the combined field
  const rawAddressData = addressAlignmentField?.fromAriba || "Not Available";
  let billToAddress = "Not Available";
  let shipToAddress = "Not Available";
  
  if (rawAddressData !== "Not Available" && rawAddressData.includes("Ship:")) {
    const parts = rawAddressData.split("Ship:");
    billToAddress = parts[0].replace("Bill:", "").replace(/[,|]\s*$/, "").trim();
    shipToAddress = parts[1].trim();
  } else {
    billToAddress = rawAddressData;
  }

  const attachmentColumns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Type",
      key: "type",
      width: 100,
      align: "center",
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {record.icon}
          {record.typeText}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      align: "center",
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleFileView(record.fileName)}
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleFileDownload(record.fileName)}
          >
            Download
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="pr-details-container">
      <div className="pr-details-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="back-button"
        >
          Back to Dashboard
        </Button>
        <div className="flex-between" style={{ width: "100%" }}>
          <Title level={3} className="pr-details-title">
            <FileTextOutlined /> Purchase Request Details - {prNo}
            <Button
              type="link"
              icon={<SearchOutlined />}
              onClick={handleShowValidation}
              style={{
                marginLeft: "16px",
                fontSize: "14px",
                textDecoration: "underline",
                color: "#2121c2",
              }}
            >
              {matchedFields}/{totalFields} Fields Availability
            </Button>
          </Title>
          <div className="pr-header-actions">
            <div className="assignee-section">
              <Typography.Text>Assignee:</Typography.Text>
              <Typography.Text strong>{selectedAssignee}</Typography.Text>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => setShowAssigneeModal(true)}
              />
            </div>
            <div className="approval-section">
              <Typography.Text strong className="approval-section-title">
                Submit Purchase Request
              </Typography.Text>
              <div className="approval-buttons">
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => handleApproval("Approve")}
                  style={{ backgroundColor: "#52c41a" }}
                  disabled={actionLoading}
                >
                  Approve
                </Button>
                <Button
                  type="default"
                  icon={<PauseOutlined />}
                  onClick={() => handleApproval("Hold")}
                  style={{ backgroundColor: "#faad14", color: "white" }}
                  disabled={actionLoading}
                >
                  Hold
                </Button>
                <Button
                  type="primary"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleApproval("Reject")}
                  disabled={actionLoading}
                >
                  Reject
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {hasInvoiceAttachment && (
        <div style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #ff4d4f',
          color: '#991b1b',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontWeight: 'bold',
          fontSize: '14px'
        }}>
          This PR contains invoice type attachments
        </div>
      )}

      {hasNonEnglishAttachment && (
        <div style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #ff4d4f',
          color: '#991b1b',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontWeight: 'bold',
          fontSize: '14px'
        }}>
          This PR contains attachments in a language other than English
        </div>
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card className="pr-info-card" title="Request Information">
            <Descriptions bordered labelStyle={{ width: '180px' }} contentStyle={{ minWidth: '200px' }}>
              <Descriptions.Item label="PR Number">
                <strong>{prDetails.pr_number}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Status" labelStyle={{ width: '150px' }}>
                {getStatusTag(displayStatus)}
              </Descriptions.Item>
              <Descriptions.Item label="Title">
                {prDetails.title}
              </Descriptions.Item>
              <Descriptions.Item label="Assignee">
                <UserOutlined /> {prDetails.assignee_name}
              </Descriptions.Item>
              <Descriptions.Item label="Processed Date" labelStyle={{ width: '150px' }}>
                <CalendarOutlined /> {prDetails.processed_date || '--'}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier">
                {prDetails.supplier_names}
              </Descriptions.Item>
              
            
              <Descriptions.Item label="Total Amount">
                <DollarOutlined /> {parseFloat(prDetails.total_amount).toFixed(2)} 
              </Descriptions.Item>
              <Descriptions.Item label="Submit Date" labelStyle={{ width: '150px' }}>
                <CalendarOutlined /> {prDetails.po_submit_date || '--'}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Term">
                {getPaymentTerms()}
              </Descriptions.Item>
              <Descriptions.Item label="Supporting Document Availability" labelStyle={{ width: '280px' }}>
                {prDetails.attachments_available || '--'}
              </Descriptions.Item>
              <Descriptions.Item label="Attachment Count">
                {prDetails.attachment_count ?? '--'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {(prDetails.commodity_code || prDetails.line_items) && (
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card className="pr-info-card" title="Commodity Code">
              <Descriptions bordered column={2} labelStyle={{ width: '150px' }}>
                <Descriptions.Item label="Commodity Code" labelStyle={{ width: '170px' }} contentStyle={{ minWidth: '300px' }}>
                  {prDetails.commodity_code || '--'}
                </Descriptions.Item>
                <Descriptions.Item label="Commodity Code Attachment" labelStyle={{ width: '250px' }} contentStyle={{ minWidth: '300px' }}>
                  {prDetails.commodity_code_attachment || '--'}
                </Descriptions.Item>
                {prDetails.line_items && (
                  <>
                    <Descriptions.Item label="Ariba Value" labelStyle={{ width: '150px' }} contentStyle={{ minWidth: '300px' }}>
                      {prDetails.line_items.ariba_value || '--'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Attachment Value" labelStyle={{ width: '200px' }} contentStyle={{ minWidth: '300px' }}>
                      {prDetails.line_items.attachment_value || '--'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Feedback">
                      {prDetails.line_items.feedback || '--'}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card className="pr-items-card" title="Requested Items">
            <Table
              dataSource={prDetails.items_with_amount?.map((item, index) => ({
                key: index,
                description: item.description,
                quantity: item.quantity,
                amount: item.amount,
              }))}
              columns={[
                {
                  title: "Description",
                  dataIndex: "description",
                  key: "description",
                },
                {
                  title: "Quantity",
                  dataIndex: "quantity",
                  key: "quantity",
                  width: 100,
                  align: "center",
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  width: 120,
                  align: "right",
                  render: (amount) => amount ? `${prDetails.currency || '$'} ${amount}` : 'N/A',
                },
                {
                  title: "Amount Per Line Item",
                  key: "lineItemAmount",
                  width: 200,
                  align: "right",
                  render: (_, record) => {
                    const total = (record.quantity || 0) * (record.amount || 0);
                    return `${prDetails.currency || '$'} ${total.toFixed(2)}`;
                  },
                },
              ]}
              pagination={false}
              size="small"
              summary={() => {
                const totalAmount = prDetails.items_with_amount?.reduce((sum, item) => {
                  return sum + ((item.quantity || 0) * (item.amount || 0));
                }, 0) || 0;
                
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3} align="right">
                        <strong>Total Amount:</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong>{prDetails.currency || '$'} {totalAmount.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
          {hasAttachments && (
            <Card className="attachments-card" title="Attachments">
              <Table
                columns={attachmentColumns}
                dataSource={attachmentsData}
                pagination={false}
                className="attachments-table"
                size="small"
              />
            </Card>
          )}
          {hasNestedAttachments && (
            <Card className="attachments-card" title="Nested Attachments">
              <Table
                columns={attachmentColumns}
                dataSource={nestedAttachmentsData}
                pagination={false}
                className="attachments-table"
                size="small"
              />
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          <Card className="pr-score-card" title="Field Availability-Match Index">
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div>
                <Typography.Text
                  strong
                  style={{ marginBottom: "8px", display: "block" }}
                >
                  Field Availability
                </Typography.Text>
                <div className="score-progress-row">
                  <Button
                    type="link"
                    onClick={handleShowValidation}
                    style={{
                      padding: 0,
                      height: 'auto',
                      fontSize: '14px',
                      color: '#2121c2'
                    }}
                  >
                    <span style={{ textDecoration: 'underline' }}>
                      {matchedFields}/{totalFields} Fields Availability
                    </span>
                    {' '}
                    <span style={{ color: getScoreColor(matchPercentage), textDecoration: 'none' }}>({matchPercentage}%)</span>
                  </Button>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography.Text strong>Region:</Typography.Text>
                  <Tag
                    color="blue"
                    style={{ fontSize: "13px", padding: "4px 8px" }}
                  >
                    {prDetails.bill_to_region || '--'}
                  </Tag>
                </div>
              </div>

              <div>
                <Typography.Text strong>Amount Check</Typography.Text>
                <div className="score-section">
                  <Tag
                    color={amountCheckPassed ? "success" : "error"}
                    style={{ fontSize: "13px", padding: "4px 8px" }}
                  >
                    {amountCheckPassed ? "✓" : "✗"} {isUnder50k ? `Under $50k (${formattedAmount})` : isExemptRegion ? `Over $50k (${formattedAmount})` : `Over $50k (${formattedAmount})`}
                  </Tag>
                </div>
              </div>

              <div>
                <Typography.Text strong>Address Verification</Typography.Text>
                <div className="score-section">
                  <Tag
                    color={addressesMatch ? "success" : "error"}
                    style={{ fontSize: "13px", padding: "4px 8px" }}
                  >
                    {addressesMatch ? "✓" : "✗"} {addressesMatch ? "Matching Addresses" : "Different Addresses"}
                  </Tag>
                  <div className="address-details">
                    <div className="address-item">
                      <strong>Bill To:</strong> {billToAddress}
                    </div>
                    <div>
                      <strong>Ship To:</strong> {shipToAddress}
                    </div>
                  </div>
                </div>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Field Validation Checks"
        open={false}
        style={{ top: 20 }}
        onCancel={() => setShowChecksModal(false)}
        footer={null}
        width={1400}
      >
        <Spin spinning={validationLoading} tip="Loading validation data...">
          <Table
            columns={checksColumns}
            dataSource={validationData}
            pagination={false}
            size="small"
            scroll={{ y: 400 }}
            style={{ fontSize: "12px" }}
          />
        </Spin>
      </Modal>

      <Modal
        title={`${approvalAction} Purchase Request`}
        open={showApprovalModal}
        onOk={handleApprovalSubmit}
        onCancel={() => { if (!actionLoading) setShowApprovalModal(false); }}
        okText={approvalAction}
        confirmLoading={actionLoading}
        closable={!actionLoading}
        maskClosable={!actionLoading}
        cancelButtonProps={{ disabled: actionLoading }}
      >
        <div className="modal-form-item">
          <div className="ai-recommendation-section">
            <div className="recommendation-item">
              <Typography.Text strong>AI Recommendation: </Typography.Text>
              <Tag color="blue" style={{ marginLeft: '8px' }}>Approve</Tag>
            </div>
            <div className="recommendation-item">
              <Typography.Text strong>Your Selection: </Typography.Text>
              <Tag
                className={`selection-tag-${approvalAction.toLowerCase()}`}
                style={{ marginLeft: '8px' }}
              >
                {approvalAction}
              </Tag>
            </div>
          </div>
          <label>Notes *:</label>
          <Input.TextArea
            rows={4}
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder={`AI suggested Approve. You selected ${approvalAction}`}
            disabled={actionLoading}
          />
          {actionLoading && (
            <div style={{ marginTop: '12px', color: '#1890ff', fontSize: '13px' }}>
              Processing your request... This may take a few minutes as the system searches pending PRs.
            </div>
          )}
          {approvalError && (
            <div className="modal-error-text">
              {approvalError}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title="Change Assignee"
        open={showAssigneeModal}
        onOk={handleAssigneeChange}
        onCancel={() => setShowAssigneeModal(false)}
      >
        <div className="modal-form-item">
          <label>Select New Assignee:</label>
          <Select
            style={{ width: "100%", marginTop: "8px" }}
            value={selectedAssignee}
            onChange={setSelectedAssignee}
            options={assigneeOptions}
          />
        </div>
      </Modal>
    </div>
  );
};

export default PRDetails;
